import React, { forwardRef, memo, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import useButtonTracking from '../../hooks/useButtonTracking';

/* PROPERTIES
  ------------------
  Common Properties:
  - id
  - outline
  - color
  - className
  - style
  - onClick
  - active
  - disabled
  - block
  - title
  - type
  - children

  ------------------
  FOR, BUTTON TAG:
  - ignoreButtonClass

  ------------------
  FOR, LINK TAG:
  - to
  - isLinkTag
  - isLinkTagIncludeButtonClass

  ------------------
  FOR, ANCHOR TAG:
  - to
  - target
  - isAnchorTag
  - isAnchorTagIncludeButtonClass
*/

const CustomButton = forwardRef(
  (
    {
      id = '',
      outline = false,
      size = 'sm',
      color = 'secondary',
      className = '',
      style = null,
      onClick,
      active = false,
      disabled = false,
      block = false,
      title = null,
      type = 'button',
      target = null,
      children,
      ignoreButtonClass = false,
      //
      to = '',
      //
      isLinkTag = false,
      isLinkTagIncludeButtonClass = false,
      //
      iscopyButton = false,
      copyText = '',
      copyTextButton = '',
      //
      isAnchorTag = false,
      isAnchorTagIncludeButtonClass = false,
      ...rest
    },
    ref
  ) => {
    const buttonId = id ? id?.toString()?.trim() : null;
    const [isCopied, setIsCopied] = useState(false);
    const trackClick = useButtonTracking();
    title = title === null && typeof children === 'string' ? children : title;

    const copyToClipboard = async () => {
      try {
        // await navigator.clipboard.writeText(copyText);
        const container = document.createElement('div');
        container.innerHTML = copyText;
        container.style.position = 'absolute';
        container.style.left = '-9999px'; // Position off-screen
        document.body.appendChild(container);

        // Select all content inside the container
        const range = document.createRange();
        range.selectNodeContents(container);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        // Execute copy command
        document.execCommand('copy');
        document.body.removeChild(container);
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 1200);
      } catch (error) {
        setIsCopied(false);
        console.error('Unable to copy text: ', error);
      }
    };

    const kind = isAnchorTag ? 'anchor' : isLinkTag ? 'link' : iscopyButton ? 'copy' : 'button';
    const label =
      typeof children === 'string' ? children : typeof title === 'string' ? title : undefined;

    const handleClick = event => {
      trackClick({ id: buttonId, label, kind, to: to || undefined, target, variant: color });
      if (iscopyButton) return copyToClipboard();
      if (onClick) return onClick(event);
    };
    const getCleanClassNameFn = () => {
      const buttonSizeClass = size ? `btn-${size}` : '';
      // const buttonSizeClass = size ? `btn-${size} ripple ripple-dark` : '';
      const buttonBlockLevelClass = block ? 'btn-block' : '';
      const buttonDisabledClass = disabled ? 'disabled' : '';
      const buttonActiveClass = active ? 'active' : '';
      const buttonColorClass = color ? `btn-${color}` : '';
      const buttonOutlineClass = outline ? `btn-outline-${color}` : '';
      const buttonFinalColorClass = !outline ? buttonColorClass : buttonOutlineClass;

      let addClass = '';
      if (isAnchorTag) {
        if (isAnchorTagIncludeButtonClass) {
          addClass = `btn ${buttonSizeClass} ${buttonFinalColorClass} ${buttonActiveClass} ${buttonDisabledClass} ${buttonBlockLevelClass} ${className}`;
        } else {
          addClass = `${buttonActiveClass} ${buttonDisabledClass} ${buttonBlockLevelClass} ${className}`;
        }
      } else if (isLinkTag) {
        if (isLinkTagIncludeButtonClass) {
          addClass = `btn ${buttonSizeClass} ${buttonFinalColorClass} ${buttonActiveClass} ${buttonDisabledClass} ${buttonBlockLevelClass} ${className}`;
        } else {
          addClass = `${buttonActiveClass} ${buttonDisabledClass} ${buttonBlockLevelClass} ${className}`;
        }
      } else if (ignoreButtonClass) {
        addClass = `${buttonActiveClass} ${buttonDisabledClass} ${buttonBlockLevelClass} ${className}`;
      } else {
        addClass = `btn ${buttonSizeClass} ${buttonFinalColorClass} ${buttonActiveClass} ${buttonDisabledClass} ${buttonBlockLevelClass} ${className}`;
      }

      const cleanedClassName = addClass?.replace(/\s+/g, ' ')?.trim();
      return cleanedClassName;
    };

    // React.useEffect(() => {
    //   const addRippleEffect = ev => {
    //     const rippleContainer = ev.currentTarget;
    //     const tag = document.createElement('span');
    //     tag.className = 'ripple-animation';

    //     const rect = rippleContainer.getBoundingClientRect();
    //     const size = Math.max(rect.width, rect.height);
    //     const posX = ev.clientX - rect.left - size / 2;
    //     const posY = ev.clientY - rect.top - size / 2;

    //     tag.style.width = tag.style.height = `${size}px`;
    //     tag.style.left = `${posX}px`;
    //     tag.style.top = `${posY}px`;

    //     rippleContainer.appendChild(tag);

    //     // Cleanup the added span after the animation completes
    //     tag.addEventListener('animationend', () => {
    //       tag.parentNode.removeChild(tag);
    //     });
    //   };

    //   const rippleContainers = document.querySelectorAll('.ripple');

    //   rippleContainers.forEach(ripplecontainer => {
    //     ripplecontainer.addEventListener('click', addRippleEffect);
    //   });

    //   // Clean up event listeners on component unmount
    //   return () => {
    //     rippleContainers.forEach(ripplecontainer => {
    //       ripplecontainer.removeEventListener('click', addRippleEffect);
    //     });
    //   };
    // }, []);

    const getClassName = getCleanClassNameFn();

    if (isAnchorTag) {
      return (
        <a
          ref={ref}
          id={buttonId}
          href={to}
          style={style}
          className={getClassName}
          role={type}
          title={title}
          aria-pressed={active}
          target={target}
          onClick={handleClick}
          {...rest}
        >
          {children}
        </a>
      );
    }

    if (iscopyButton) {
      return (
        <button
          ref={ref}
          id={buttonId}
          type={type}
          style={style}
          className={getClassName}
          // onClick={handleClick}
          title={title}
          disabled={disabled}
          {...rest}
          onClick={handleClick}
        >
          {!isCopied ? (
            children
          ) : copyTextButton === '' ? (
            <>
              <i className="fa fa-check" /> Copied to clipboard!
            </>
          ) : (
            copyTextButton
          )}
        </button>
      );
    }
    if (isLinkTag) {
      return (
        <Link
          ref={ref}
          id={buttonId}
          to={to}
          style={style}
          className={getClassName}
          title={title}
          disabled={disabled}
          onClick={handleClick}
          {...rest}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        id={buttonId}
        type={type}
        style={style}
        className={getClassName}
        onClick={handleClick}
        title={title}
        disabled={disabled}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

CustomButton.propTypes = {
  id: PropTypes.string,
  outline: PropTypes.any,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  color: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
  to: PropTypes.string,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  block: PropTypes.bool,
  type: PropTypes.string,
  title: PropTypes.any,
  style: PropTypes.any,
  target: PropTypes.any,
  isLinkTag: PropTypes.bool,
  ignoreButtonClass: PropTypes.bool,
  isAnchorTag: PropTypes.bool,
  copyText: PropTypes.any,
  copyTextButton: PropTypes.any,
  iscopyButton: PropTypes.bool,
  isAnchorTagIncludeButtonClass: PropTypes.bool,
  children: PropTypes.any
};

CustomButton.displayName = 'CustomButton';
export default memo(CustomButton);
